import React, { useState, useEffect, useMemo } from 'react';
import { Search, Copy, Download, Shuffle, Eye, Settings, Filter, Target, Zap } from 'lucide-react';

interface ExtractionMethod {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

interface ExtractionOptions {
  caseSensitive: boolean;
  wholeWordsOnly: boolean;
  useRegex: boolean;
  includeLineNumbers: boolean;
  includeSurroundingContext: boolean;
  contextLength: number;
  removeDuplicates: boolean;
  sortResults: boolean;
  reverseSort: boolean;
  minLength: number;
  maxLength: number;
  extractUnique: boolean;
  preserveOrder: boolean;
  addLineBreaks: boolean;
  customSeparator: string;
  highlightMatches: boolean;
  showStatistics: boolean;
}

interface ExtractionResult {
  originalText: string;
  extractedItems: ExtractedItem[];
  formattedOutput: string;
  statistics: ExtractionStats;
}

interface ExtractedItem {
  value: string;
  lineNumber: number;
  position: number;
  context: string;
  length: number;
}

interface ExtractionStats {
  totalMatches: number;
  uniqueMatches: number;
  duplicatesRemoved: number;
  averageLength: number;
  longestMatch: string;
  shortestMatch: string;
  linesWithMatches: number;
  totalLines: number;
  extractionRate: number;
}

const TextExtractor: React.FC = () => {
  const [inputText, setInputText] = useState('');
  const [extractionPattern, setExtractionPattern] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('pattern');
  const [outputText, setOutputText] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [options, setOptions] = useState<ExtractionOptions>({
    caseSensitive: false,
    wholeWordsOnly: false,
    useRegex: false,
    includeLineNumbers: false,
    includeSurroundingContext: false,
    contextLength: 20,
    removeDuplicates: false,
    sortResults: false,
    reverseSort: false,
    minLength: 0,
    maxLength: 1000,
    extractUnique: false,
    preserveOrder: true,
    addLineBreaks: true,
    customSeparator: '\n',
    highlightMatches: false,
    showStatistics: false,
  });

  const extractionMethods: ExtractionMethod[] = [
    {
      id: 'pattern',
      name: 'Pattern Matching',
      description: 'Extract text matching a specific pattern',
      icon: <Search className="w-4 h-4" />,
      color: 'blue'
    },
    {
      id: 'emails',
      name: 'Email Addresses',
      description: 'Extract all email addresses',
      icon: <Target className="w-4 h-4" />,
      color: 'green'
    },
    {
      id: 'urls',
      name: 'URLs/Links',
      description: 'Extract web URLs and links',
      icon: <Target className="w-4 h-4" />,
      color: 'purple'
    },
    {
      id: 'phones',
      name: 'Phone Numbers',
      description: 'Extract phone numbers in various formats',
      icon: <Target className="w-4 h-4" />,
      color: 'red'
    },
    {
      id: 'numbers',
      name: 'Numbers',
      description: 'Extract numeric values',
      icon: <Target className="w-4 h-4" />,
      color: 'yellow'
    },
    {
      id: 'dates',
      name: 'Dates',
      description: 'Extract date patterns',
      icon: <Target className="w-4 h-4" />,
      color: 'indigo'
    },
    {
      id: 'words',
      name: 'Words',
      description: 'Extract individual words',
      icon: <Filter className="w-4 h-4" />,
      color: 'pink'
    },
    {
      id: 'lines',
      name: 'Lines Containing',
      description: 'Extract lines containing pattern',
      icon: <Filter className="w-4 h-4" />,
      color: 'orange'
    },
    {
      id: 'quoted',
      name: 'Quoted Text',
      description: 'Extract text in quotes',
      icon: <Filter className="w-4 h-4" />,
      color: 'teal'
    },
    {
      id: 'brackets',
      name: 'Bracketed Text',
      description: 'Extract text in brackets/parentheses',
      icon: <Filter className="w-4 h-4" />,
      color: 'cyan'
    },
    {
      id: 'hashtags',
      name: 'Hashtags',
      description: 'Extract hashtags and mentions',
      icon: <Target className="w-4 h-4" />,
      color: 'emerald'
    },
    {
      id: 'custom',
      name: 'Custom Regex',
      description: 'Use custom regular expressions',
      icon: <Settings className="w-4 h-4" />,
      color: 'violet'
    }
  ];

  const performExtraction = (text: string, pattern: string, method: string, opts: ExtractionOptions): ExtractionResult => {
    if (!text) {
      return {
        originalText: text,
        extractedItems: [],
        formattedOutput: '',
        statistics: createEmptyStats()
      };
    }

    try {
      setErrorMessage('');
      
      let extractedItems: ExtractedItem[] = [];

      switch (method) {
        case 'pattern':
          extractedItems = extractPattern(text, pattern, opts);
          break;
        case 'emails':
          extractedItems = extractEmails(text, opts);
          break;
        case 'urls':
          extractedItems = extractUrls(text, opts);
          break;
        case 'phones':
          extractedItems = extractPhones(text, opts);
          break;
        case 'numbers':
          extractedItems = extractNumbers(text, opts);
          break;
        case 'dates':
          extractedItems = extractDates(text, opts);
          break;
        case 'words':
          extractedItems = extractWords(text, pattern, opts);
          break;
        case 'lines':
          extractedItems = extractLines(text, pattern, opts);
          break;
        case 'quoted':
          extractedItems = extractQuoted(text, opts);
          break;
        case 'brackets':
          extractedItems = extractBracketed(text, opts);
          break;
        case 'hashtags':
          extractedItems = extractHashtags(text, opts);
          break;
        case 'custom':
          extractedItems = extractCustomRegex(text, pattern, opts);
          break;
        default:
          extractedItems = extractPattern(text, pattern, opts);
      }

      // Apply post-processing
      extractedItems = postProcessItems(extractedItems, opts);

      // Generate formatted output
      const formattedOutput = formatOutput(extractedItems, opts);

      // Calculate statistics
      const statistics = calculateExtractionStats(text, extractedItems);

      return {
        originalText: text,
        extractedItems,
        formattedOutput,
        statistics
      };

    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'An error occurred during extraction');
      return {
        originalText: text,
        extractedItems: [],
        formattedOutput: '',
        statistics: createEmptyStats()
      };
    }
  };

  const extractPattern = (text: string, pattern: string, opts: ExtractionOptions): ExtractedItem[] => {
    if (!pattern) return [];

    const lines = text.split('\n');
    const items: ExtractedItem[] = [];
    
    lines.forEach((line, lineIndex) => {
      let searchText = opts.caseSensitive ? line : line.toLowerCase();
      let searchPattern = opts.caseSensitive ? pattern : pattern.toLowerCase();

      if (opts.useRegex) {
        const flags = opts.caseSensitive ? 'g' : 'gi';
        const regex = new RegExp(pattern, flags);
        let match;
        
        while ((match = regex.exec(line)) !== null) {
          items.push({
            value: match[0],
            lineNumber: lineIndex + 1,
            position: match.index,
            context: getContext(line, match.index, match[0].length, opts.contextLength),
            length: match[0].length
          });
        }
      } else {
        if (opts.wholeWordsOnly) {
          const regex = new RegExp(`\\b${escapeRegex(searchPattern)}\\b`, opts.caseSensitive ? 'g' : 'gi');
          let match;
          
          while ((match = regex.exec(line)) !== null) {
            items.push({
              value: match[0],
              lineNumber: lineIndex + 1,
              position: match.index,
              context: getContext(line, match.index, match[0].length, opts.contextLength),
              length: match[0].length
            });
          }
        } else {
          let startIndex = 0;
          let index;
          
          while ((index = searchText.indexOf(searchPattern, startIndex)) !== -1) {
            const actualMatch = line.substring(index, index + pattern.length);
            items.push({
              value: actualMatch,
              lineNumber: lineIndex + 1,
              position: index,
              context: getContext(line, index, pattern.length, opts.contextLength),
              length: actualMatch.length
            });
            startIndex = index + 1;
          }
        }
      }
    });

    return items;
  };

  const extractEmails = (text: string, opts: ExtractionOptions): ExtractedItem[] => {
    const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
    return extractWithRegex(text, emailRegex, opts);
  };

  const extractUrls = (text: string, opts: ExtractionOptions): ExtractedItem[] => {
    const urlRegex = /https?:\/\/[^\s<>"{}|\\^`[\]]+/g;
    return extractWithRegex(text, urlRegex, opts);
  };

  const extractPhones = (text: string, opts: ExtractionOptions): ExtractedItem[] => {
    const phoneRegex = /(?:\+?1[-.\s]?)?\(?[0-9]{3}\)?[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}\b/g;
    return extractWithRegex(text, phoneRegex, opts);
  };

  const extractNumbers = (text: string, opts: ExtractionOptions): ExtractedItem[] => {
    const numberRegex = /\b\d+(?:\.\d+)?\b/g;
    return extractWithRegex(text, numberRegex, opts);
  };

  const extractDates = (text: string, opts: ExtractionOptions): ExtractedItem[] => {
    const dateRegex = /\b\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}\b|\b\d{4}[\/\-]\d{1,2}[\/\-]\d{1,2}\b/g;
    return extractWithRegex(text, dateRegex, opts);
  };

  const extractWords = (text: string, pattern: string, opts: ExtractionOptions): ExtractedItem[] => {
    if (pattern) {
      // Extract words matching pattern
      return extractPattern(text, pattern, opts);
    } else {
      // Extract all words
      const wordRegex = /\b\w+\b/g;
      return extractWithRegex(text, wordRegex, opts);
    }
  };

  const extractLines = (text: string, pattern: string, opts: ExtractionOptions): ExtractedItem[] => {
    if (!pattern) return [];

    const lines = text.split('\n');
    const items: ExtractedItem[] = [];
    
    lines.forEach((line, lineIndex) => {
      let shouldInclude = false;
      
      if (opts.useRegex) {
        const flags = opts.caseSensitive ? '' : 'i';
        const regex = new RegExp(pattern, flags);
        shouldInclude = regex.test(line);
      } else {
        const searchText = opts.caseSensitive ? line : line.toLowerCase();
        const searchPattern = opts.caseSensitive ? pattern : pattern.toLowerCase();
        shouldInclude = searchText.includes(searchPattern);
      }

      if (shouldInclude) {
        items.push({
          value: line,
          lineNumber: lineIndex + 1,
          position: 0,
          context: line,
          length: line.length
        });
      }
    });

    return items;
  };

  const extractQuoted = (text: string, opts: ExtractionOptions): ExtractedItem[] => {
    const quotedRegex = /"([^"]*?)"|'([^']*?)'/g;
    const items: ExtractedItem[] = [];
    const lines = text.split('\n');

    lines.forEach((line, lineIndex) => {
      let match;
      while ((match = quotedRegex.exec(line)) !== null) {
        const value = match[1] || match[2]; // Get content without quotes
        items.push({
          value,
          lineNumber: lineIndex + 1,
          position: match.index,
          context: getContext(line, match.index, match[0].length, opts.contextLength),
          length: value.length
        });
      }
    });

    return items;
  };

  const extractBracketed = (text: string, opts: ExtractionOptions): ExtractedItem[] => {
    const bracketRegex = /\[([^\]]*)\]|\(([^)]*)\)|\{([^}]*)\}/g;
    const items: ExtractedItem[] = [];
    const lines = text.split('\n');

    lines.forEach((line, lineIndex) => {
      let match;
      while ((match = bracketRegex.exec(line)) !== null) {
        const value = match[1] || match[2] || match[3]; // Get content without brackets
        items.push({
          value,
          lineNumber: lineIndex + 1,
          position: match.index,
          context: getContext(line, match.index, match[0].length, opts.contextLength),
          length: value.length
        });
      }
    });

    return items;
  };

  const extractHashtags = (text: string, opts: ExtractionOptions): ExtractedItem[] => {
    const hashtagRegex = /#\w+|@\w+/g;
    return extractWithRegex(text, hashtagRegex, opts);
  };

  const extractCustomRegex = (text: string, pattern: string, opts: ExtractionOptions): ExtractedItem[] => {
    if (!pattern) return [];
    
    const flags = `g${opts.caseSensitive ? '' : 'i'}`;
    const regex = new RegExp(pattern, flags);
    return extractWithRegex(text, regex, opts);
  };

  const extractWithRegex = (text: string, regex: RegExp, opts: ExtractionOptions): ExtractedItem[] => {
    const items: ExtractedItem[] = [];
    const lines = text.split('\n');

    lines.forEach((line, lineIndex) => {
      let match;
      const lineRegex = new RegExp(regex.source, regex.flags);
      
      while ((match = lineRegex.exec(line)) !== null) {
        items.push({
          value: match[0],
          lineNumber: lineIndex + 1,
          position: match.index,
          context: getContext(line, match.index, match[0].length, opts.contextLength),
          length: match[0].length
        });
      }
    });

    return items;
  };

  const getContext = (line: string, position: number, length: number, contextLength: number): string => {
    const start = Math.max(0, position - contextLength);
    const end = Math.min(line.length, position + length + contextLength);
    return line.substring(start, end);
  };

  const escapeRegex = (str: string): string => {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const postProcessItems = (items: ExtractedItem[], opts: ExtractionOptions): ExtractedItem[] => {
    let processed = [...items];

    // Filter by length
    processed = processed.filter(item => 
      item.length >= opts.minLength && item.length <= opts.maxLength
    );

    // Remove duplicates
    if (opts.removeDuplicates) {
      const seen = new Set<string>();
      processed = processed.filter(item => {
        const key = opts.caseSensitive ? item.value : item.value.toLowerCase();
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });
    }

    // Extract unique only
    if (opts.extractUnique) {
      const counts = new Map<string, number>();
      processed.forEach(item => {
        const key = opts.caseSensitive ? item.value : item.value.toLowerCase();
        counts.set(key, (counts.get(key) || 0) + 1);
      });
      processed = processed.filter(item => {
        const key = opts.caseSensitive ? item.value : item.value.toLowerCase();
        return counts.get(key) === 1;
      });
    }

    // Sort results
    if (opts.sortResults) {
      processed = processed.sort((a, b) => {
        const comparison = opts.caseSensitive 
          ? a.value.localeCompare(b.value)
          : a.value.toLowerCase().localeCompare(b.value.toLowerCase());
        return opts.reverseSort ? -comparison : comparison;
      });
    } else if (!opts.preserveOrder) {
      // Sort by line number and position if not preserving order
      processed = processed.sort((a, b) => {
        if (a.lineNumber !== b.lineNumber) {
          return a.lineNumber - b.lineNumber;
        }
        return a.position - b.position;
      });
    }

    return processed;
  };

  const formatOutput = (items: ExtractedItem[], opts: ExtractionOptions): string => {
    if (items.length === 0) return '';

    let formatted = items.map((item, index) => {
      let line = '';

      if (opts.includeLineNumbers) {
        line += `Line ${item.lineNumber}: `;
      }

      line += item.value;

      if (opts.includeSurroundingContext && item.context !== item.value) {
        line += ` (context: "${item.context}")`;
      }

      return line;
    });

    return formatted.join(opts.addLineBreaks ? '\n' : opts.customSeparator);
  };

  const calculateExtractionStats = (originalText: string, items: ExtractedItem[]): ExtractionStats => {
    const totalMatches = items.length;
    const uniqueValues = new Set(items.map(item => item.value.toLowerCase()));
    const uniqueMatches = uniqueValues.size;
    const duplicatesRemoved = totalMatches - uniqueMatches;

    const lengths = items.map(item => item.length);
    const averageLength = lengths.length > 0 
      ? Math.round(lengths.reduce((sum, len) => sum + len, 0) / lengths.length)
      : 0;

    const longestMatch = items.reduce((longest, item) => 
      item.value.length > longest.length ? item.value : longest, ''
    );
    const shortestMatch = items.reduce((shortest, item) => 
      item.value.length < shortest.length ? item.value : shortest, items[0]?.value || ''
    );

    const linesWithMatches = new Set(items.map(item => item.lineNumber)).size;
    const totalLines = originalText.split('\n').length;
    const extractionRate = totalLines > 0 ? Math.round((linesWithMatches / totalLines) * 100) : 0;

    return {
      totalMatches,
      uniqueMatches,
      duplicatesRemoved,
      averageLength,
      longestMatch,
      shortestMatch,
      linesWithMatches,
      totalLines,
      extractionRate
    };
  };

  const createEmptyStats = (): ExtractionStats => ({
    totalMatches: 0,
    uniqueMatches: 0,
    duplicatesRemoved: 0,
    averageLength: 0,
    longestMatch: '',
    shortestMatch: '',
    linesWithMatches: 0,
    totalLines: 0,
    extractionRate: 0
  });

  const extractionResult: ExtractionResult = useMemo(() => {
    return performExtraction(inputText, extractionPattern, selectedMethod, options);
  }, [inputText, extractionPattern, selectedMethod, options]);

  useEffect(() => {
    setOutputText(extractionResult.formattedOutput);
  }, [extractionResult]);

  const copyToClipboard = async () => {
    if (outputText) {
      await navigator.clipboard.writeText(outputText);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const downloadResult = () => {
    if (outputText) {
      const summary = `Text Extraction Results:
Method: ${extractionMethods.find(m => m.id === selectedMethod)?.name}
Pattern: "${extractionPattern}"
Total Matches: ${extractionResult.statistics.totalMatches}
Unique Matches: ${extractionResult.statistics.uniqueMatches}
Lines with Matches: ${extractionResult.statistics.linesWithMatches}
Extraction Rate: ${extractionResult.statistics.extractionRate}%

Extracted Text:
${outputText}`;

      const blob = new Blob([summary], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'text-extraction-results.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const generateSampleText = () => {
    const samples = [
      `Contact Information:
John Doe - john.doe@example.com - (555) 123-4567
Jane Smith - jane.smith@company.org - +1-555-987-6543
Visit our website: https://www.example.com
Follow us: @company #innovation`,
      `Project dates: 01/15/2024 to 03/30/2024
Budget: $15,000 - $25,000
Phone: 555.123.4567
Email: project@company.com
Website: https://project.example.com`,
      `"This is a quoted text" and here's another 'single quoted text'.
Numbers to extract: 123, 45.67, 890
[Bracketed content] and (parentheses content) and {curly braces content}
#hashtag1 #hashtag2 @mention1 @mention2`,
      `Sales Report 2024-01-15:
Q1 Revenue: $1,250,000
Q2 Revenue: $1,450,000
Contact: sales@company.com
Phone: (555) 999-8888
Visit: https://sales.company.com`,
      `Log Entry [2024-01-15 10:30:00]: User login
"Error occurred" at line 245
Phone number found: +1-555-123-4567
Email notifications sent to: admin@system.com
URL accessed: https://api.service.com/v1/data`,
      `Meeting Notes 12/01/2023:
Attendees: alice@company.com, bob@company.com
"Action items" include contacting 555-0123
Budget range: $10,000 to $50,000
[URGENT] Review by 12/15/2023`
    ];
    setInputText(samples[Math.floor(Math.random() * samples.length)]);
  };

  const getColorClasses = (color: string) => {
    const colorMap = {
      blue: 'bg-blue-50 border-blue-200 text-blue-800',
      green: 'bg-green-50 border-green-200 text-green-800',
      purple: 'bg-purple-50 border-purple-200 text-purple-800',
      red: 'bg-red-50 border-red-200 text-red-800',
      yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
      pink: 'bg-pink-50 border-pink-200 text-pink-800',
      orange: 'bg-orange-50 border-orange-200 text-orange-800',
      teal: 'bg-teal-50 border-teal-200 text-teal-800',
      cyan: 'bg-cyan-50 border-cyan-200 text-cyan-800',
      emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
      violet: 'bg-violet-50 border-violet-200 text-violet-800'
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.blue;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-green-50 to-emerald-100 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2 flex items-center justify-center gap-3">
            <Search className="text-teal-600" />
            Advanced Text Extractor
          </h1>
          <p className="text-gray-600">Extract specific patterns and data from text with powerful filtering options</p>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-700">Extraction Configuration</h2>
            <div className="flex gap-2">
              <button
                onClick={generateSampleText}
                className="flex items-center gap-2 px-3 py-1 bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200 transition-colors text-sm"
              >
                <Shuffle size={16} />
                Sample Text
              </button>
              <button
                onClick={() => setShowOptions(!showOptions)}
                className="flex items-center gap-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm"
              >
                <Settings size={16} />
                Options
              </button>
              <button
                onClick={() => setShowStats(!showStats)}
                className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
              >
                <Eye size={16} />
                Statistics
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-2">
                {selectedMethod === 'custom' ? 'Regex Pattern' : 
                 ['emails', 'urls', 'phones', 'numbers', 'dates', 'hashtags', 'quoted', 'brackets'].includes(selectedMethod) ? 
                 'Auto-detection (no pattern needed)' : 'Search Pattern'}
              </label>
              <input
                type="text"
                value={extractionPattern}
                onChange={(e) => setExtractionPattern(e.target.value)}
                placeholder={
                  selectedMethod === 'custom' ? 'Enter regex pattern...' :
                  selectedMethod === 'pattern' ? 'Enter text pattern to find...' :
                  selectedMethod === 'words' ? 'Enter word pattern (optional)...' :
                  selectedMethod === 'lines' ? 'Enter text that lines should contain...' :
                  ['emails', 'urls', 'phones', 'numbers', 'dates', 'hashtags', 'quoted', 'brackets'].includes(selectedMethod) ?
                  'Pattern not required' : 'Enter search pattern...'
                }
                disabled={['emails', 'urls', 'phones', 'numbers', 'dates', 'hashtags', 'quoted', 'brackets'].includes(selectedMethod)}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none disabled:bg-gray-100"
              />
            </div>
            <div className="flex items-end">
              <div className="text-sm text-gray-600">
                <p>Method: <span className="font-semibold">{extractionMethods.find(m => m.id === selectedMethod)?.name}</span></p>
                <p>Matches: <span className="font-semibold text-teal-600">{extractionResult.statistics.totalMatches}</span></p>
              </div>
            </div>
          </div>

          {errorMessage && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <Zap className="text-red-600" size={16} />
              <span className="text-red-700 text-sm">{errorMessage}</span>
            </div>
          )}
        </div>

        {/* Extraction Options */}
        {showOptions && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <Settings className="text-purple-600" />
              <h2 className="text-xl font-semibold text-gray-800">Extraction Options</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Search Options</h3>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.caseSensitive}
                    onChange={(e) => setOptions({...options, caseSensitive: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Case Sensitive</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.wholeWordsOnly}
                    onChange={(e) => setOptions({...options, wholeWordsOnly: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Whole Words Only</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.useRegex}
                    onChange={(e) => setOptions({...options, useRegex: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Use Regular Expression</span>
                </label>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Min Length</label>
                  <input
                    type="number"
                    min="0"
                    value={options.minLength}
                    onChange={(e) => setOptions({...options, minLength: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Max Length</label>
                  <input
                    type="number"
                    min="1"
                    value={options.maxLength}
                    onChange={(e) => setOptions({...options, maxLength: parseInt(e.target.value) || 1000})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-sm"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Result Processing</h3>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.removeDuplicates}
                    onChange={(e) => setOptions({...options, removeDuplicates: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Remove Duplicates</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.extractUnique}
                    onChange={(e) => setOptions({...options, extractUnique: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Extract Unique Only</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.sortResults}
                    onChange={(e) => setOptions({...options, sortResults: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Sort Results</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.reverseSort}
                    onChange={(e) => setOptions({...options, reverseSort: e.target.checked})}
                    disabled={!options.sortResults}
                    className="w-4 h-4 text-purple-600 disabled:opacity-50"
                  />
                  <span className="text-sm">Reverse Sort</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.preserveOrder}
                    onChange={(e) => setOptions({...options, preserveOrder: e.target.checked})}
                    disabled={options.sortResults}
                    className="w-4 h-4 text-purple-600 disabled:opacity-50"
                  />
                  <span className="text-sm">Preserve Original Order</span>
                </label>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Output Format</h3>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.includeLineNumbers}
                    onChange={(e) => setOptions({...options, includeLineNumbers: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Include Line Numbers</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.includeSurroundingContext}
                    onChange={(e) => setOptions({...options, includeSurroundingContext: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Include Context</span>
                </label>

                {options.includeSurroundingContext && (
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Context Length</label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={options.contextLength}
                      onChange={(e) => setOptions({...options, contextLength: parseInt(e.target.value) || 20})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-sm"
                    />
                  </div>
                )}

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={options.addLineBreaks}
                    onChange={(e) => setOptions({...options, addLineBreaks: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Add Line Breaks</span>
                </label>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Custom Separator</label>
                  <input
                    type="text"
                    value={options.customSeparator}
                    onChange={(e) => setOptions({...options, customSeparator: e.target.value})}
                    placeholder="Custom separator..."
                    disabled={options.addLineBreaks}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-sm disabled:bg-gray-100"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Input and Output */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-lg font-semibold text-gray-700">Source Text</label>
                <div className="text-sm text-gray-500">
                  {extractionResult.statistics.totalLines} lines
                </div>
              </div>
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Enter text to extract data from..."
                className="w-full h-64 p-4 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none resize-none text-gray-700 transition-colors font-mono text-sm"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-lg font-semibold text-gray-700">Extracted Results</label>
                <div className="flex items-center gap-2">
                  <div className="text-sm text-gray-500">
                    {extractionResult.statistics.totalMatches} matches
                  </div>
                  <button
                    onClick={copyToClipboard}
                    disabled={!outputText}
                    className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm disabled:opacity-50"
                  >
                    <Copy size={16} />
                    {copySuccess ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={downloadResult}
                    disabled={!outputText}
                    className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm disabled:opacity-50"
                  >
                    <Download size={16} />
                    Download
                  </button>
                </div>
              </div>
              <textarea
                value={outputText}
                readOnly
                className="w-full h-64 p-4 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-700 resize-none font-mono text-sm"
                placeholder="Extracted text will appear here..."
              />
            </div>
          </div>
        </div>

        {/* Extraction Methods */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <Search className="text-teal-600" />
            <h2 className="text-xl font-semibold text-gray-800">Choose Extraction Method</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {extractionMethods.map((method) => (
              <div
                key={method.id}
                onClick={() => setSelectedMethod(method.id)}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg ${
                  selectedMethod === method.id
                    ? `border-${method.color}-500 ${getColorClasses(method.color)} shadow-md`
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {method.icon}
                    <h3 className={`font-semibold ${
                      selectedMethod === method.id ? '' : 'text-gray-800'
                    }`}>
                      {method.name}
                    </h3>
                  </div>
                  {selectedMethod === method.id && (
                    <div className={`w-3 h-3 bg-${method.color}-500 rounded-full`}></div>
                  )}
                </div>
                <p className={`text-sm ${
                  selectedMethod === method.id ? '' : 'text-gray-600'
                }`}>
                  {method.description}
                </p>
              </div>
            ))}
          </div>

          {/* Method Help */}
          <div className="mt-6 p-4 bg-gray-50 rounded-xl">
            <h3 className="font-semibold text-gray-700 mb-2">
              {extractionMethods.find(m => m.id === selectedMethod)?.name} Help
            </h3>
            <div className="text-sm text-gray-600">
              {selectedMethod === 'pattern' && (
                <div>
                  <p>• Extract text matching a specific pattern or keyword</p>
                  <p>• Use options for case sensitivity and whole word matching</p>
                </div>
              )}
              {selectedMethod === 'emails' && (
                <div>
                  <p>• Automatically detects email addresses</p>
                  <p>• Matches standard email formats like user@domain.com</p>
                </div>
              )}
              {selectedMethod === 'urls' && (
                <div>
                  <p>• Extracts web URLs and links</p>
                  <p>• Matches http:// and https:// URLs</p>
                </div>
              )}
              {selectedMethod === 'phones' && (
                <div>
                  <p>• Detects phone numbers in various formats</p>
                  <p>• Matches (555) 123-4567, 555-123-4567, +1-555-123-4567</p>
                </div>
              )}
              {selectedMethod === 'numbers' && (
                <div>
                  <p>• Extracts numeric values from text</p>
                  <p>• Matches integers and decimal numbers</p>
                </div>
              )}
              {selectedMethod === 'dates' && (
                <div>
                  <p>• Finds date patterns in text</p>
                  <p>• Matches MM/DD/YYYY, MM-DD-YYYY, YYYY-MM-DD formats</p>
                </div>
              )}
              {selectedMethod === 'words' && (
                <div>
                  <p>• Extracts individual words</p>
                  <p>• Optional pattern to filter specific words</p>
                </div>
              )}
              {selectedMethod === 'lines' && (
                <div>
                  <p>• Extracts entire lines containing the pattern</p>
                  <p>• Useful for filtering log entries or specific content</p>
                </div>
              )}
              {selectedMethod === 'quoted' && (
                <div>
                  <p>• Extracts text within quotes</p>
                  <p>• Matches both "double quotes" and 'single quotes'</p>
                </div>
              )}
              {selectedMethod === 'brackets' && (
                <div>
                  <p>• Extracts text within brackets or parentheses</p>
                  <p>• Matches [square], (round), and curly brackets</p>
                </div>
              )}
              {selectedMethod === 'hashtags' && (
                <div>
                  <p>• Extracts hashtags and mentions</p>
                  <p>• Matches #hashtags and @mentions from social media</p>
                </div>
              )}
              {selectedMethod === 'custom' && (
                <div>
                  <p>• Use custom regular expressions</p>
                  <p>• Advanced pattern matching with full regex support</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Statistics */}
        {showStats && inputText && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <Eye className="text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-800">Extraction Statistics</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-2">Total Matches</h3>
                <div className="text-2xl font-bold text-blue-900">{extractionResult.statistics.totalMatches}</div>
                <p className="text-sm text-blue-600">Found instances</p>
              </div>

              <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                <h3 className="font-semibold text-green-800 mb-2">Unique Matches</h3>
                <div className="text-2xl font-bold text-green-900">{extractionResult.statistics.uniqueMatches}</div>
                <p className="text-sm text-green-600">Distinct values</p>
              </div>

              <div className="p-4 bg-orange-50 rounded-xl border border-orange-200">
                <h3 className="font-semibold text-orange-800 mb-2">Lines with Matches</h3>
                <div className="text-2xl font-bold text-orange-900">{extractionResult.statistics.linesWithMatches}</div>
                <p className="text-sm text-orange-600">Of {extractionResult.statistics.totalLines} total</p>
              </div>

              <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                <h3 className="font-semibold text-purple-800 mb-2">Extraction Rate</h3>
                <div className="text-2xl font-bold text-purple-900">{extractionResult.statistics.extractionRate}%</div>
                <p className="text-sm text-purple-600">Lines with matches</p>
              </div>
            </div>

            {/* Additional Stats */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Average Length</h4>
                <div className="text-lg font-bold text-gray-900">{extractionResult.statistics.averageLength} chars</div>
              </div>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Duplicates</h4>
                <div className="text-lg font-bold text-gray-900">{extractionResult.statistics.duplicatesRemoved}</div>
              </div>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Method</h4>
                <div className="text-sm font-bold text-gray-900">{extractionMethods.find(m => m.id === selectedMethod)?.name}</div>
              </div>
            </div>

            {/* Match Analysis */}
            {extractionResult.statistics.longestMatch && extractionResult.statistics.shortestMatch && (
              <div className="mt-6">
                <h3 className="font-semibold text-gray-700 mb-3">Match Analysis:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <h4 className="font-semibold text-emerald-800 mb-1">
                      Longest Match ({extractionResult.statistics.longestMatch.length} chars)
                    </h4>
                    <div className="text-sm text-emerald-700 font-mono truncate max-w-full">
                      {extractionResult.statistics.longestMatch}
                    </div>
                  </div>
                  <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
                    <h4 className="font-semibold text-teal-800 mb-1">
                      Shortest Match ({extractionResult.statistics.shortestMatch.length} chars)
                    </h4>
                    <div className="text-sm text-teal-700 font-mono truncate max-w-full">
                      {extractionResult.statistics.shortestMatch}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Results Preview */}
        {extractionResult.extractedItems.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <Target className="text-green-600" />
              <h2 className="text-xl font-semibold text-gray-800">Extraction Results Preview</h2>
            </div>

            <div className="space-y-2 max-h-64 overflow-y-auto mb-6">
              {extractionResult.extractedItems.slice(0, 20).map((item, index) => (
                <div key={index} className="p-2 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <span className="text-xs text-gray-500 font-mono min-w-[3rem]">#{index + 1}</span>
                    <span className="text-sm font-mono text-gray-700 truncate max-w-md">
                      {item.value}
                    </span>
                    {options.includeLineNumbers && (
                      <span className="text-xs text-blue-600">Line {item.lineNumber}</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    {item.length} chars
                  </div>
                </div>
              ))}
              {extractionResult.extractedItems.length > 20 && (
                <div className="text-sm text-gray-500 text-center py-2">
                  ...and {extractionResult.extractedItems.length - 20} more matches
                </div>
              )}
            </div>

            {/* Summary */}
            <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
              <h3 className="font-semibold text-teal-700 mb-2">Extraction Summary</h3>
              <div className="text-sm text-teal-800 space-y-1">
                <div>Method: <span className="font-semibold">{extractionMethods.find(m => m.id === selectedMethod)?.name}</span></div>
                {extractionPattern && (
                  <div>Pattern: <span className="font-mono bg-white px-2 py-1 rounded">"{extractionPattern}"</span></div>
                )}
                <div>Found: <span className="font-semibold">{extractionResult.statistics.totalMatches} matches</span> in <span className="font-semibold">{extractionResult.statistics.linesWithMatches} lines</span></div>
                <div>Unique: <span className="font-semibold">{extractionResult.statistics.uniqueMatches} distinct values</span></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TextExtractor;